import type RTCDataView from './data-view'

interface AVCNalu {
  type: number;
  // 这里不是nalu header，这只是annexb或avcc的分割符。比如 0x000001
  header: Uint8Array;
  // 这里实际上包含了 nalu header + body
  body: Uint8Array;
  ndr?: boolean;
  idr?: boolean;
  sei?: boolean;
  sps?: boolean;
  pps?: boolean;
}

const Nalunit = {
  // https://en.wikipedia.org/wiki/Network_Abstraction_Layer
  getNALUnits (buffer: RTCDataView, isH265 = false): AVCNalu[] {
    if (buffer.length - buffer.position < 4) {
      return []
    }

    const { position } = buffer
    // 0x001 || 0x0001
    if (
      buffer.getInt32(position) === 1 ||
      (buffer.getInt16(position) === 0 && buffer.getInt8(position + 2) === 1)
    ) {
      return Nalunit.getAnnexbNals(buffer, isH265)
    } else {
      return Nalunit.getAvccNals(buffer, isH265)
    }
  },

  getAnnexbNals (buffer: RTCDataView, isH265: boolean): AVCNalu[] {
    const nals = []
    let position = Nalunit.getHeaderPositionAnnexB(buffer)
    let start = position.pos
    let end = start
    while (start < buffer.length - 4) {
      const header = new Uint8Array(
        buffer.buffer.slice(start, start + position.headerLength)
      )
      if (position.pos === buffer.position) {
        buffer.skip(position.headerLength)
      }
      position = Nalunit.getHeaderPositionAnnexB(buffer)
      end = position.pos
      const body = new Uint8Array(buffer.buffer.slice(start + header.byteLength, end))
      const unit = { header, body, type: -1 }
      isH265 ? Nalunit.analyseH265Nal(unit) : Nalunit.analyseNal(unit)
      if ((unit.type <= 9 || (isH265 && unit.type <= 40)) && unit.type !== 0) {
        nals.push(unit)
      }
      buffer.skip(end - buffer.position)
      start = end
    }
    return nals
  },

  getAvccNals (buffer: RTCDataView, isH265: boolean) {
    // buffer.buffer = RBSP.EBSP2RBSP(new Uint8Array(buffer.buffer)).buffer;
    // buffer.dataview = new DataView(buffer.buffer)
    // buffer.dataview.position = 0;
    const nals = []
    while (buffer.position < buffer.length - 4) {
      const length = buffer.getInt32(buffer.position)
      if (buffer.length - buffer.position >= length) {
        const header = new Uint8Array(
          buffer.buffer.slice(buffer.position, buffer.position + 4)
        )
        buffer.skip(4)
        const body = new Uint8Array(
          buffer.buffer.slice(buffer.position, buffer.position + length)
        )
        buffer.skip(length)
        const unit = { header, body, type: -1 }
        isH265 ? Nalunit.analyseH265Nal(unit) : Nalunit.analyseNal(unit)
        if (unit.type <= 9 && unit.type !== 0) {
          nals.push(unit)
        }
      } else {
        break
      }
    }
    return nals
  },

  // * ISO-14496-10 7.3.1
  // *  forbidden_zero_bit  1bit
  // *  nal_ref_idc  2bit
  // *  nal_unit_type 5bit
  analyseNal (unit: AVCNalu) {
    const type = unit.body[0] & 0x1f
    unit.type = type
    switch (type) {
      case 1:
        // NDR
        unit.ndr = true
        break
      case 5:
        // IDR
        unit.idr = true
        break
      case 6:
        // SEI
        unit.sei = true
        break
      case 7:
        // SPS
        unit.sps = true
        break
      case 8:
        // PPS
        unit.pps = true
        break
      case 9:
        // AUD
        break
      default:
        break
    }
  },

  analyseH265Nal (unit: AVCNalu) {
    const type = (unit.body[0] & 0b01111110) >> 1
    unit.type = type
    switch (type) {
      case 39:
      case 40:
        // SEI
        unit.sei = true
        break
      default:
        break
    }
  },

  getHeaderPositionAnnexB (buffer: RTCDataView) {
    // seperate
    let pos = buffer.position
    let headerLength = 0
    const bufferLen = buffer.length
    while (headerLength !== 3 && headerLength !== 4 && pos < bufferLen - 4) {
      if (buffer.getInt16(pos) === 0) {
        if (buffer.getInt16(pos + 2) === 1) {
          // 0x000001
          headerLength = 4
        } else if (buffer.getInt8(pos + 2) === 1) {
          headerLength = 3
        } else {
          pos++
        }
      } else {
        pos++
      }
    }

    if (pos === bufferLen - 4) {
      if (buffer.getInt16(pos) === 0) {
        if (buffer.getInt16(pos + 2) === 1) {
          // 0x000001
          headerLength = 4
        } else {
          pos = bufferLen
        }
      } else {
        pos++
        if (buffer.getInt16(pos) === 0 && buffer.getInt8(pos) === 1) {
          // 0x0000001
          headerLength = 3
        } else {
          pos = bufferLen
        }
      }
    }
    return { pos, headerLength }
  },

  isH265VideoFrame (chunk: RTCEncodedVideoFrame) {
    // @ts-expect-error 实际有这个属性，但tslint不认识
    return (chunk.getMetadata?.()?.mimeType || '').toLowerCase().includes('h265')
  }
}

export default Nalunit
